import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link, useBlocker } from 'react-router';
import { ArrowLeft, Code2, Monitor, Redo2, Smartphone, TriangleAlert, Undo2 } from 'lucide-react';
import {
  moveSection,
  restoreSection,
  type SectionDocument,
  type SectionEditModel,
  type SectionView,
} from '@nopain-newsletter/core/sections';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Sheet, SheetBody, SheetContent } from '../ui/Sheet';
import { MockBadge } from '../MockBadge';
import { useMediaQuery } from '../campaign-editor/hooks';
import { cn } from '../../lib/utils';
import { SectionCanvas, type CanvasDevice } from './SectionCanvas';
import { SectionInspector } from './SectionInspector';
import { SectionOutline } from './SectionOutline';
import { slotLabel } from './sectionText';
import { useSectionEditor, type SaveState, type SectionSaveResult } from './useSectionEditor';

/** Asked before an in-app link or the browser's back/forward button leaves unsaved edits. */
const LEAVE_QUESTION = 'You have section edits that are not saved. Leave without saving?';

/** Below this width the inspector opens as a side panel instead of a third column. */
const NARROW_QUERY = '(max-width: 1099px)';

function saveLabel(save: SaveState): string {
  switch (save.status) {
    case 'clean':
      return 'No changes';
    case 'dirty':
      return 'Unsaved changes';
    case 'saving':
      return 'Saving…';
    case 'saved':
      return `Saved at ${new Date(save.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    case 'error':
      return 'Not saved';
  }
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  const tag = el?.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable === true
  );
}

function IconToggle({
  pressed,
  label,
  onClick,
  children,
}: {
  pressed: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium',
        pressed ? 'bg-card text-text shadow-sm' : 'text-muted hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The section editor: the sections of the mail on the left, the mail in
 * the middle, the selected section on the right. It only holds the edit
 * model; every change is a core operation and the server judges the save.
 */
export function SectionEditorScreen({
  title,
  back,
  htmlHref,
  doc,
  original,
  initialModel,
  readOnly,
  readOnlyReason,
  notice,
  onSave,
  onReload,
}: {
  title: string;
  back: { to: string; label: string };
  /** Where the HTML source of this mail is edited (the way out of this editor). */
  htmlHref: string;
  doc: SectionDocument;
  original: string;
  initialModel: SectionEditModel;
  readOnly: boolean;
  readOnlyReason?: string | undefined;
  /** Something the page wants said above the mail (for example that images were not copied). */
  notice?: ReactNode;
  onSave: (model: SectionEditModel, originalHash: string) => Promise<SectionSaveResult>;
  /** Fetches the mail again after the server said the original changed. */
  onReload: () => void;
}) {
  const editor = useSectionEditor({ doc, initialModel, onSave });
  const { view, save } = editor;
  const narrow = useMediaQuery(NARROW_QUERY);
  const [device, setDevice] = useState<CanvasDevice>('desktop');
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [problemsOpen, setProblemsOpen] = useState(false);
  const [moveNotice, setMoveNotice] = useState<string | null>(null);

  const section = view.sections.find((s) => s.id === selectedSection) ?? null;
  const slotOwner = useMemo(() => {
    const map = new Map<string, SectionView>();
    for (const s of view.sections) for (const slot of s.slots) map.set(slot.id, s);
    return map;
  }, [view]);

  const select = useCallback(
    (sectionId: string | null, slotId: string | null) => {
      // A slot outside the rows (the frame of the mail) has no section marker of its own.
      const owner = slotId !== null ? slotOwner.get(slotId) : undefined;
      const target = owner?.id ?? sectionId;
      setSelectedSection(target);
      setSelectedSlot(owner ? slotId : null);
      setPanelOpen(target !== null);
    },
    [slotOwner],
  );

  const { undo, redo, saveNow, dirty } = editor;
  const canSave = !readOnly && dirty && save.status !== 'saving';
  const onShortcut = useCallback(
    (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === 's') {
        event.preventDefault();
        if (canSave) void saveNow();
      } else if (key === 'z' && !isTyping(event.target)) {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if (key === 'y' && !isTyping(event.target)) {
        event.preventDefault();
        redo();
      }
    },
    [canSave, saveNow, undo, redo],
  );
  useEffect(() => {
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, [onShortcut]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** Links that leave the editor inside the app: with unsaved edits, ask first. */
  const onLeave = (event: MouseEvent<HTMLAnchorElement>) => {
    if (dirty && !window.confirm(LEAVE_QUESTION)) event.preventDefault();
  };

  // The browser's back and forward buttons (a POP) do not pass through the
  // links above, so the router holds them while there are unsaved edits.
  //
  // The router calls this from a function it registers in an effect, which runs
  // after the DOM has already changed. Reading `dirty` from a closure would let
  // a back press right after "Saved at" appears still see the old, dirty value
  // and ask a question that no longer applies; a ref is current from the render.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) =>
      dirtyRef.current &&
      historyAction === 'POP' &&
      currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm(LEAVE_QUESTION)) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  const mailProblems = view.problems.filter((p) => p.slotId === undefined);
  const slotProblems = view.problems.filter((p) => p.slotId !== undefined);
  const stale = save.status === 'error' && save.stale;

  const inspector = section ? (
    <SectionInspector
      key={section.id}
      section={section}
      editor={editor}
      selectedSlot={selectedSlot}
      readOnly={readOnly}
      onSelectSlot={setSelectedSlot}
      onSelectSection={(id) => {
        setSelectedSection(id);
        setSelectedSlot(null);
        if (id === null) setPanelOpen(false);
      }}
    />
  ) : null;

  return (
    <div className="flex h-dvh min-h-0 flex-col bg-bg">
      <header className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-card px-4 py-2.5">
        <Link
          to={back.to}
          onClick={onLeave}
          aria-label={back.label}
          title={back.label}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-text hover:bg-bg"
        >
          <ArrowLeft size={18} aria-hidden="true" />
        </Link>
        <div className="min-w-0 flex-shrink">
          <h1 className="m-0 truncate text-[15px] font-semibold">{title}</h1>
          <p className="m-0 text-xs text-muted" role="status" data-save={save.status}>
            <span
              aria-hidden="true"
              className={cn(
                'mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle',
                save.status === 'error'
                  ? 'bg-danger-text'
                  : dirty
                    ? 'bg-warning-marker'
                    : 'bg-success-bar',
              )}
            />
            {readOnly ? 'Read only' : saveLabel(save)}
          </p>
        </div>
        <MockBadge />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Undo"
            title="Undo"
            disabled={readOnly || !editor.canUndo}
            onClick={undo}
          >
            <Undo2 size={16} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Redo"
            title="Redo"
            disabled={readOnly || !editor.canRedo}
            onClick={redo}
          >
            <Redo2 size={16} aria-hidden="true" />
          </Button>
          <div
            role="group"
            aria-label="Preview width"
            className="flex rounded-[10px] border border-border bg-bg p-0.5"
          >
            <IconToggle
              pressed={device === 'desktop'}
              label="Desktop"
              onClick={() => setDevice('desktop')}
            >
              <Monitor size={15} aria-hidden="true" />
              <span className="hidden sm:inline">Desktop</span>
            </IconToggle>
            <IconToggle
              pressed={device === 'mobile'}
              label="Mobile"
              onClick={() => setDevice('mobile')}
            >
              <Smartphone size={15} aria-hidden="true" />
              <span className="hidden sm:inline">Mobile</span>
            </IconToggle>
          </div>
          {view.problems.length > 0 ? (
            <Button
              variant="secondary"
              size="sm"
              className="border-warning-marker/40 bg-warning-bg text-warning-text hover:bg-warning-bg"
              onClick={() => setProblemsOpen(true)}
            >
              <TriangleAlert size={15} aria-hidden="true" />
              {view.problems.length} {view.problems.length === 1 ? 'problem' : 'problems'}
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" asChild>
            <Link to={htmlHref} onClick={onLeave} className="no-underline">
              <Code2 size={15} aria-hidden="true" />
              Edit HTML
            </Link>
          </Button>
          <Button size="sm" disabled={!canSave} onClick={() => void saveNow()}>
            {save.status === 'saving' ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </header>

      <div
        className={cn(
          'min-h-0 flex-grow',
          narrow ? 'flex flex-col' : 'grid grid-cols-[296px_minmax(0,1fr)_344px]',
        )}
      >
        <aside
          className={cn(
            'min-h-0 overflow-y-auto bg-card p-3',
            narrow ? 'max-h-[34vh] flex-shrink-0 border-b border-border' : 'border-r border-border',
          )}
        >
          <SectionOutline
            view={view}
            selected={selectedSection}
            readOnly={readOnly}
            onSelect={(id) => {
              setSelectedSection(id);
              setSelectedSlot(null);
              setPanelOpen(true);
            }}
            onMove={(id, toIndex) => {
              const result = editor.apply((d, m) => moveSection(d, m, id, toIndex));
              setMoveNotice(result.ok ? null : result.error.message);
            }}
            onRestore={(id) => {
              const result = editor.apply((d, m) => restoreSection(d, m, id));
              setMoveNotice(result.ok ? null : result.error.message);
            }}
          />
          {moveNotice !== null ? <Alert className="mt-3">{moveNotice}</Alert> : null}
        </aside>

        <main className="flex min-h-0 min-w-0 flex-grow flex-col">
          <div className="flex flex-shrink-0 flex-col gap-2 px-4 pt-3 empty:hidden">
            {readOnly && readOnlyReason ? <Alert>{readOnlyReason}</Alert> : null}
            {notice}
            {doc.structure === 'none' ? (
              <p className="m-0 rounded-xl border border-border bg-card px-4 py-3 text-sm">
                <strong className="font-semibold">This mail cannot be cut into sections.</strong>{' '}
                {doc.structureReason} Only its texts, images and links can be changed here; nothing
                can be moved, hidden or removed.
              </p>
            ) : null}
            {mailProblems.length > 0 ? (
              <ul className="m-0 flex list-none flex-col gap-1 rounded-xl bg-warning-bg px-4 py-3 text-sm text-warning-text">
                {mailProblems.map((problem) => (
                  <li key={problem.code}>
                    <span aria-hidden="true">! </span>
                    {problem.message}
                  </li>
                ))}
              </ul>
            ) : null}
            {save.status === 'error' ? (
              <Alert>
                <p className="m-0">{save.message}</p>
                {save.errors.length > 0 ? (
                  <ul className="m-0 mt-1 pl-4">
                    {save.errors.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                ) : null}
                {stale ? (
                  <Button variant="secondary" size="sm" className="mt-2" onClick={onReload}>
                    Reload the mail
                  </Button>
                ) : null}
              </Alert>
            ) : null}
            {editor.unmapped.length > 0 ? (
              <Alert>
                <p className="m-0">
                  Saved, but some changes no longer fit the mail and were left out:
                </p>
                <ul className="m-0 mt-1 pl-4">
                  {editor.unmapped.map((entry, i) => (
                    <li key={`${entry.code}:${entry.id ?? i}`}>{entry.message}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}
          </div>
          <div className="relative min-h-[320px] flex-grow basis-0">
            <div className="absolute inset-4">
              <SectionCanvas
                doc={doc}
                original={original}
                model={editor.model}
                view={view}
                device={device}
                selectedSection={selectedSection}
                selectedSlot={selectedSlot}
                onSelect={select}
                onKeyDown={onShortcut}
              />
            </div>
          </div>
        </main>

        {narrow ? null : (
          <aside
            aria-label="Selected section"
            className="min-h-0 overflow-y-auto border-l border-border bg-card p-4"
          >
            {inspector ?? (
              <p className="m-0 text-sm text-muted">
                Select a section in the list, or click a text, an image or a link in the mail.
              </p>
            )}
          </aside>
        )}
      </div>

      {narrow ? (
        <Sheet open={panelOpen && section !== null} onOpenChange={setPanelOpen}>
          <SheetContent
            eyebrow="Section"
            title={section?.name ?? 'Section'}
            description="Change the texts, images and links of this section."
          >
            <SheetBody>{inspector}</SheetBody>
          </SheetContent>
        </Sheet>
      ) : null}

      <Sheet open={problemsOpen} onOpenChange={setProblemsOpen}>
        <SheetContent
          eyebrow="Mail"
          title="Problems"
          description="What the check found in the mail as it is now. Choose one to go to it."
        >
          <SheetBody>
            {mailProblems.length > 0 ? (
              <div>
                <h3 className="m-0 mb-1.5 text-[13px] font-medium text-muted">The whole mail</h3>
                <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
                  {mailProblems.map((problem) => (
                    <li key={problem.code} className="rounded-lg bg-warning-bg px-3 py-2">
                      {problem.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {slotProblems.length > 0 ? (
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {slotProblems.map((problem, i) => {
                  const owner = slotOwner.get(problem.slotId ?? '');
                  const slot = owner?.slots.find((s) => s.id === problem.slotId);
                  return (
                    <li key={`${problem.slotId}:${problem.code}:${i}`}>
                      <button
                        type="button"
                        className="w-full rounded-lg border border-border bg-card px-3 py-2 text-left hover:bg-bg"
                        onClick={() => {
                          setProblemsOpen(false);
                          select(owner?.id ?? null, problem.slotId ?? null);
                        }}
                      >
                        <span className="block text-xs text-muted">
                          {owner?.name ?? 'Mail'}
                          {slot ? ` · ${slotLabel(slot).kind}: ${slotLabel(slot).value}` : ''}
                        </span>
                        <span className="block text-sm">{problem.message}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}
